import { useShopI18n } from '../i18n/ShopI18n'
import { useEffect, useId, useRef, useState } from 'react'
import type { Extra, ShopItem } from '../types'
import { useAssetUrl } from '../hooks/useAsset'
import { productArtwork, shopArtwork, hotDealDesigns } from './shopArtwork'
import { describeShopItem, formatShopAmount, formatShopExtraPresentation, formatShopPrice } from '../utils/shopFormatters'

interface ShopItemCardProps {
    item: ShopItem
    onPurchase: () => void
    size?: 'normal' | 'large'
    width?: string
    height?: string
    hot?: boolean
    designIndex?: number
    highlighted?: boolean
    initiallyExpanded?: boolean
    purchaseLimit?: { maximum: number; purchased: number }
}

function bonusIcon(extra: Extra) {
    const type = `${extra.type} ${extra.goodName}`.toLowerCase()
    if (/league|liga/.test(type)) {
        return shopArtwork.league
    }

    if (/loyal|treue/.test(type)) {
        return shopArtwork.loyalty
    }

    if (/joker/.test(type)) {
        return shopArtwork.joker
    }

    if (/chips/.test(type)) {
        return shopArtwork.coin
    }

    return shopArtwork.trophy
}

function BonusIcon({ extra }: { extra: Extra }) {
    const { language } = useShopI18n()
    const url = useAssetUrl(extra.icon?.length ? extra.icon.join('/') : null, { language })
    const [failed, setFailed] = useState<string | null>(null)
    return <img src={url && url !== failed ? url : bonusIcon(extra)} alt="" onError={() => setFailed(url)} />
}

export default function ShopItemCard({
    item,
    onPurchase,
    hot = false,
    designIndex = 0,
    highlighted = false,
    purchaseLimit,
    initiallyExpanded = false,
}: ShopItemCardProps) {
    const { t, locale, language } = useShopI18n()
    const [extrasExpanded, setExtrasExpanded] = useState(initiallyExpanded)
    const [now, setNow] = useState(() => Date.now())
    const cardRef = useRef<HTMLElement>(null)
    const bonusId = useId()
    const product = describeShopItem(item, locale, t)
    const { joker, amount, unit } = product
    const price = formatShopPrice(item, undefined, locale)
    const remaining = item.endDate ? Math.max(0, Math.ceil(item.endDate - now / 1000)) : null
    const expired = remaining === 0
    const limitReached = !!purchaseLimit && purchaseLimit.purchased >= purchaseLimit.maximum
    const extras = [...(item.extras ?? []), ...(item.shopBonus?.amount ? [{ type: item.type || 'chips', amountText: '', icon: [], meta: {}, goodName: item.shopBonus.title, title: item.shopBonus.title, amount: item.shopBonus.amount }] : [])]
    const ultimate = item.type === 'ultimate-pass'
    const ultimateChips = ultimate ? extras.filter(extra => extra.type.toLowerCase() === 'chips').reduce((sum, extra) => sum + (Number.isFinite(extra.amount) ? extra.amount : 0), 0) : 0
    const badge = {
        'most-popular': t('Beliebteste'),
        'best-offer': t('Bestes Angebot'),
        'is-new': t('Neu'),
    }[item.flag ?? '']
    const design = hotDealDesigns[designIndex % hotDealDesigns.length]
    const artwork = hot && !ultimate ? design.artwork : productArtwork(item)
    const calendarLogoBase = ultimate && item.calendarTheme && /^[a-z0-9-]+$/i.test(item.calendarTheme)
        ? `events/plus-calendar2/${item.calendarTheme}/main/logo` : null
    const calendarWebp = useAssetUrl(calendarLogoBase ? `${calendarLogoBase}.webp` : null, { language })
    const calendarPng = useAssetUrl(calendarLogoBase ? `${calendarLogoBase}.png` : null, { language })
    const [failedLogoUrls, setFailedLogoUrls] = useState<string[]>([])
    const displayedLogo = [calendarWebp, calendarPng, item.calendarLogoUrl, shopArtwork.ultimate]
        .find((url): url is string => !!url && !failedLogoUrls.includes(url)) || shopArtwork.ultimate

    useEffect(() => {
        if (!item.endDate) {
            return
        }

        const timer = window.setInterval(() => setNow(Date.now()), 1000)
        return () => window.clearInterval(timer)
    }, [item.endDate])

    useEffect(() => {
        if (!extrasExpanded) {
            return
        }

        const close = (event: PointerEvent) => {
            if (!cardRef.current?.contains(event.target as Node)) {
                setExtrasExpanded(false)
            }
        }
        document.addEventListener('pointerdown', close)
        return () => document.removeEventListener('pointerdown', close)
    }, [extrasExpanded])

    const time =
        remaining === null
            ? ''
            : [
                ...(remaining >= 86400 ? [t('{{count}}d', { count: Math.floor(remaining / 86400) })] : []),
                ...(remaining >= 3600
                    ? [String(Math.floor((remaining % 86400) / 3600)).padStart(2, '0')]
                    : []),
                String(Math.floor((remaining % 3600) / 60)).padStart(2, '0'),
                String(remaining % 60).padStart(2, '0'),
            ].join(':')

    return (
        <article
            ref={cardRef}
            id={`shop-item-${item.slug}`}
            tabIndex={-1}
            data-hot-design={hot && !ultimate ? design.name : undefined}
            data-chip-art={!hot && !ultimate ? artwork.match(/chips-design-(\d)/)?.[0].replace('design-', '') : undefined}
            className={`shop-card ${ultimate ? 'is-ultimate-card' : ''} ${hot ? 'is-hot' : ''} ${expired ? 'is-expired' : ''} ${highlighted ? 'is-featured' : ''} ${limitReached ? 'is-limit-reached' : ''}`}
            onKeyDown={(event) => {
                if (event.key === 'Escape') {
                    event.stopPropagation()
                    setExtrasExpanded(false)
                }
            }}
        >
            {(badge || highlighted) && (
                <span className="shop-badge" data-kind={highlighted ? 'is-new' : item.flag}>
                    {highlighted ? t('Aus deiner E-Mail') : badge}
                </span>
            )}
            <div className="shop-card-content">
                {ultimate && <div className="shop-ultimate-decoration" aria-hidden="true">
                    <img className="ultimate-deco-left" src={shopArtwork.ultimateDecoPink} alt="" />
                    <img className="ultimate-deco-bottom" src={shopArtwork.ultimateDecoOrange} alt="" />
                    <img className="ultimate-deco-top" src={shopArtwork.ultimateDecoOrange} alt="" />
                    <span>+</span><span>+</span><span>+</span><span>+</span>
                </div>}
                <div className={`shop-card-art ${joker ? 'is-joker' : ''}`} aria-hidden="true">
                    {joker && <img className="joker-back" src={artwork} alt="" />}
                    {ultimate ? <>
                        <div className={`shop-ultimate-logo ${displayedLogo !== shopArtwork.ultimate ? 'has-event-logo' : ''}`}>
                            <img src={displayedLogo} alt="" loading="lazy" onError={() => setFailedLogoUrls(urls => [...urls, displayedLogo])} />
                        </div>
                        <img className="shop-ultimate-ribbon" src={shopArtwork.ultimateRibbon} alt="" />
                    </> : <img src={artwork} alt="" loading="lazy" />}
                </div>
                <p className={`shop-amount ${amount.length > 8 ? 'is-long' : ''}`}>
                    <span>{ultimateChips > 0 ? formatShopAmount(ultimateChips, locale) : item.hideAmount ? item.title : amount}</span>
                    {!ultimate && !item.hideAmount && <span className="shop-amount-currency"><img src={product.joker ? shopArtwork.joker : shopArtwork.coin} alt={unit} /></span>}
                </p>
                <p className={`shop-unit ${!ultimate && !product.subscription ? 'shop-sr-only' : ''}`}>{ultimateChips > 0 ? t('Chips') : product.title || unit}</p>
                {product.perDay && <p className="shop-daily">{product.perDay}</p>}
                <div className="shop-bonus-tray">
                    {extras.length > 0 && (
                        <>
                            <button
                                type="button"
                                className="shop-bonus-toggle"
                                aria-label={t('Bonus für {{amount}} {{unit}}', { amount, unit })}
                                aria-expanded={extrasExpanded}
                                aria-controls={bonusId}
                                onClick={() => setExtrasExpanded((value) => !value)}
                            >
                                <span>
                                    {extras.slice(0, 3).map((extra, i) => (
                                        <BonusIcon key={i} extra={extra} />
                                    ))}
                                </span>
                                <span className={`shop-chevron ${extrasExpanded ? 'is-open' : ''}`}>
                                    <img
                                        src={
                                            extrasExpanded
                                                ? shopArtwork.bonusOpen
                                                : shopArtwork.bonusClosed
                                        }
                                        alt=""
                                    />
                                </span>
                            </button>
                            {extrasExpanded && (
                                <ul
                                    id={bonusId}
                                    tabIndex={0}
                                    className={`shop-bonus-popover ${ultimate ? 'is-ultimate' : ''}`}
                                >
                                    {ultimate && (
                                        <li className="shop-bonus-note">
                                            {t('Im Kalender freischaltbar')}
                                        </li>
                                    )}
                                    {extras.map((extra, i) => {
                                        const presentation = formatShopExtraPresentation(extra, locale)
                                        return <li key={i}>
                                            <BonusIcon extra={extra} />
                                            <span><b>{presentation.amount}</b>{' '}{presentation.label}</span>
                                        </li>
                                    })}
                                </ul>
                            )}
                        </>
                    )}
                </div>
                <button
                    className="shop-price"
                    type="button"
                    disabled={expired || limitReached || !item.price}
                    onClick={onPurchase}
                    aria-label={
                        limitReached
                            ? t('{{amount}} {{unit}}: Bereits gekauft', { amount, unit })
                            : expired
                                ? t('{{amount}} {{unit}}: Abgelaufen', { amount, unit })
                                : t('{{amount}} {{unit}} für {{price}} {{interval}} kaufen', { amount, unit, price, interval: product.interval })
                    }
                >
                    {limitReached ? (
                        t('Bereits gekauft')
                    ) : expired ? (
                        t('Abgelaufen')
                    ) : (
                        <>
                            {item.priceWas && <s>{formatShopPrice(item, item.priceWas, locale)}</s>}
                            <span>
                                {price}
                                {product.interval && <small>{product.interval}</small>}
                            </span>
                        </>
                    )}
                </button>
            </div>
            {purchaseLimit && !expired && (
                <div className="shop-card-footer shop-card-limit">
                    <span>{t('BEGRENZT')}</span>
                    <b>
                        {limitReached
                            ? t('Limit erreicht')
                            : t('Noch {{count}}× möglich', { count: purchaseLimit.maximum - purchaseLimit.purchased })}
                    </b>
                </div>
            )}
            {remaining !== null && (
                <div className="shop-card-footer">
                    {expired ? (
                        <>
                            <span>{t('ABGELAUFEN')}</span>
                            <b>{t('Neues Angebot folgt')}</b>
                        </>
                    ) : (
                        <>
                            <span>{t('NUR KURZ')}</span>
                            <b>{time}</b>
                        </>
                    )}
                </div>
            )}
        </article>
    )
}
